import { Box, Stack, type StackProps, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { youShallNotPass } from 'lib/helpers/youShallNotPass/youShallNotPass'

export interface RootProps extends StackProps {
  corners: 'rounded' | 'square'
  size: 'small' | 'medium'
  margin: 'normal' | 'none'
  fullWidth?: boolean
}

const cornersMap = {
  rounded: '5px',
  square: 0,
} as const

/**
 * Drives when the right slot drops to its own row: the row breaks once this plus the right slot's own
 * width exceeds the card. A basis rather than a min-width — the content still shrinks below it when
 * there is nothing to wrap.
 */
const CONTENT_PREFERRED_WIDTH_PX = 200

export const Root = styled(Box, {
  shouldForwardProp: youShallNotPass('corners', 'size', 'margin', 'fullWidth'),
})<RootProps>(({ theme, corners, margin, fullWidth }) => ({
  borderRadius: cornersMap[corners],
  ...(margin === 'normal' && { margin: theme.spacing(1, 0) }),
  backgroundColor: theme.tokens.ThemeColorCommonBackground,
  color: theme.tokens.ThemeColorCommonOnSurfaceSecondary,
  ...(fullWidth && { width: '100%' }),
}))

export const SlotsWrapper = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  boxSizing: 'border-box',
  padding: size === 'small' ? theme.spacing(0, 1) : theme.spacing(1),
  width: '100%',
}))

export const LeftSlot = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  maxWidth: '32px',
  margin: size === 'small' ? theme.spacing(0, 1, 0, 0) : theme.spacing(0, 1),
  flex: 0,
}))

export const FlexWrap = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  flexGrow: 1,
  // Without this the row refuses to shrink past its longest unbreakable word and pushes the actions
  // slot out through the card's edge, where the card's own overflow clips it away.
  minWidth: 0,
  rowGap: theme.spacing(0.5),
  paddingLeft: size === 'small' ? 0 : theme.spacing(1),
  flexDirection: 'row',
}))

export const ContentSlot = styled(Typography, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: `1 1 ${CONTENT_PREFERRED_WIDTH_PX}px`,
  minWidth: 0,
  // Only bites once a word is wider than the whole content column: there, breaking the word keeps the
  // card intact where overflowing it would spill text across the slots beside it.
  overflowWrap: 'anywhere',
  padding: size === 'small' ? theme.spacing(0.5, 1, 0.5, 0) : theme.spacing(0, 1, 0, 0),
  '& a': {
    ...theme.typography.inherit,
    whiteSpace: 'nowrap',
  },
}))

export const RightSlot = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  flexShrink: 0,
  marginLeft: 'auto',
  gap: theme.spacing(1),
  padding: size === 'small' ? theme.spacing(1, 0) : 0,
}))

/**
 * Mirror of `LeftSlot` on the trailing edge: sits outside `FlexWrap`, so it never joins the wrap
 * negotiation between content and the right slot. For fixed-size affordances (icon buttons, overflow
 * menus) — dropping one of those to a row of its own is never the right answer at any card width.
 */
export const ActionsSlot = styled(Stack, {
  shouldForwardProp: youShallNotPass('size'),
})<Pick<RootProps, 'size'>>(({ theme, size }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  flexShrink: 0,
  gap: theme.spacing(1),
  padding: size === 'small' ? theme.spacing(1, 0) : 0,
}))
